import { logoutUser } from "../actions/authActions";
import store from "../store";
import axios from "axios";
import { apiUrl } from "../config/config";

class IdleTimer {
  constructor() {
    this.idleThreshold = 5 * 60 * 1000; // 5 minutes for logout
    this.breakThreshold = 60 * 1000; // 1 minute before considering it a break
    this.events = [
      "mousemove",
      "mousedown",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];
    this.eventHandler = this.onActivity.bind(this);

    this.idleTimer = null;
    this.countdownInterval = null;
    this.remainingTime = this.idleThreshold;
    this.breakCheckInterval = null;
    
    // Initialize break tracking
    this.breakTime = parseFloat(localStorage.getItem('breakTime')) || 0;
    this.isOnBreak = localStorage.getItem('isOnBreak') === 'true';
    const storedBreakStartTime = localStorage.getItem('breakStartTime');
    this.breakStartTime = storedBreakStartTime ? parseInt(storedBreakStartTime) : null;
    
    // console.log('Initial break time:', this.breakTime);
    // console.log('Initial break state:', this.isOnBreak);
    // console.log('Initial break start time:', this.breakStartTime);

    this.breakTime = this.getStoredBreakTime();
    this.breakStartTime = null;
    this.isOnBreak = false;
    this.startTime = this.getStoredLoginTime() || Date.now();
    this.lastActivityTime = this.startTime; // initialize last activity time
    this.activityPercentage = 0; // 🟢 On initial login: activityPercentage = 0
    localStorage.setItem("activityPercentage", this.activityPercentage);

    // 🟢 Listen for activityPercentage changes from other tabs (real-time sync)
    window.addEventListener("storage", (event) => {
      if (event.key === "activityPercentage") {
        const newValue = parseInt(event.newValue, 10) || 0;
        if (this.activityPercentage !== newValue) {
          this.activityPercentage = newValue;
          // Optionally, update UI or trigger any listeners here for real-time effect
        }
      }
    });

    // 🟢 Listen for focus to immediately sync activityPercentage from storage
    window.addEventListener("focus", () => {
      const latest =
        parseInt(localStorage.getItem("activityPercentage"), 10) || 0;
      if (this.activityPercentage !== latest) {
        this.activityPercentage = latest;
        // Optionally, update UI or trigger any listeners here for real-time effect
      }
    });

    this.start();
    this.activityMonitor();
  }

  start() {
    this.storeLoginTime();
    this.events.forEach((event) =>
      window.addEventListener(event, this.eventHandler)
    );
    document.addEventListener(
      "visibilitychange",
      this.handleVisibility.bind(this)
    );
    this.startCountdown();
  }

  onActivity() {
    this.remainingTime = this.idleThreshold; // reset to 5 minutes
    const now = Date.now();

    // Debug logging
    // console.log('Activity detected at:', new Date(now).toLocaleTimeString());
    // console.log('Current break state:', this.isOnBreak ? 'On Break' : 'Active');
    
    // Check if returning from a break
    if (this.isOnBreak) {
      // console.log('Returning from break...');
      this.endBreak();
    }

    // Update last activity time only if at least 500ms has passed (prevent rapid counting)
    if (!this.lastActivityTime || now - this.lastActivityTime > 500) {
      const previousActivityTime = this.lastActivityTime;
      this.lastActivityTime = now;

      // Calculate active duration since login
      const loginTime = this.getStoredLoginTime() || now;
      const totalTimeInMinutes = (now - loginTime) / (60 * 1000);
      const activeTimeInMinutes = totalTimeInMinutes - (this.breakTime || 0);

      // Calculate activity percentage based on active time and breaks
      let activityPercentage = Math.min(100, (activeTimeInMinutes / totalTimeInMinutes) * 100);
      
      // Ensure activity percentage doesn't drop too quickly
      const currentPercentage = parseInt(localStorage.getItem("activityPercentage"), 10) || 0;
      const maxDrop = 5; // Maximum percentage drop allowed
      activityPercentage = Math.max(activityPercentage, currentPercentage - maxDrop);

      // If user is actively working, boost slightly
      if (previousActivityTime && (now - previousActivityTime) < 30000) { // Active in last 30 seconds
        activityPercentage = Math.min(100, activityPercentage + 1);
      }

      // Round to whole number
      activityPercentage = Math.round(activityPercentage);
      
      this.activityPercentage = activityPercentage;
      localStorage.setItem("activityPercentage", Math.round(activityPercentage).toString());
      
      // console.log('Updated activity:', {
      //   lastActivityTime: new Date(this.lastActivityTime).toLocaleTimeString(),
      //   totalTimeInMinutes: Math.round(totalTimeInMinutes),
      //   activeTimeInMinutes: Math.round(activeTimeInMinutes),
      //   activityPercentage: Math.round(activityPercentage),
      //   breakTime: Math.round(this.breakTime)
      // });
    }
  }

  startCountdown() {
    this.clearTimers();

    // Main countdown for logout
    this.countdownInterval = setInterval(() => {
      this.remainingTime -= 1000;
      this.storeRemainingTime();

      if (this.remainingTime <= 0) {
        this.logout();
      }
    }, 1000);

    // Separate interval for break checking (check every second)
    this.breakCheckInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - this.lastActivityTime;

      // Start break exactly at 1 minute of inactivity
      if (timeSinceLastActivity >= this.breakThreshold) {
        if (!this.isOnBreak) {
          this.startBreak();
        }
      }
    }, 1000);
  }

  clearTimers() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    if (this.breakCheckInterval) {
      clearInterval(this.breakCheckInterval);
    }
  }

  storeRemainingTime() {
    const minutesLeft = (this.remainingTime / 1000 / 60).toFixed(2);
    localStorage.setItem("remainingLogoutTime", minutesLeft);
  }

  storeLoginTime() {
    if (!localStorage.getItem("loggedInTime")) {
      localStorage.setItem("loggedInTime", new Date().toISOString());
    }
  }

  getStoredLoginTime() {
    const time = localStorage.getItem("loggedInTime");
    return time ? new Date(time).getTime() : null;
  }

  storeBreakTime() {
    // Store break time with 2 decimal places
    const breakTimeToStore = Math.round(this.breakTime * 100) / 100;
    localStorage.setItem("breakTime", breakTimeToStore.toString());
    // console.log('Stored break time:', breakTimeToStore, 'minutes');
  }

  getStoredBreakTime() {
    const storedTime = Math.round(parseFloat(localStorage.getItem("breakTime")) * 100) / 100 || 0;
    // console.log('Retrieved stored break time:', storedTime, 'minutes');
    return storedTime;
  }

  handleVisibility() {
    if (!document.hidden) {
      this.onActivity();
    }
  }

  async logout() {
    this.clearTimers();

    const verifierId = localStorage.getItem("verifierid");
    const verifierType = localStorage.getItem("verifiertype");

    if (
      verifierId === "5d6df589c3d92110fccfde7c" ||
      verifierType === "7" ||
      verifierId == "688d9d4acebbc60f077de5e2" ||
      verifierId == "688d9c64cebbc60f077de5de"
    ) {
      // console.log("Verifier type 7 - logout skipped");
      return;
    }

    try {
      await axios.post(`${apiUrl}api/users/updateLogoutTime`, {
        Verifier_id: verifierId,
        LoginedTime: localStorage.getItem("loginedDateTime"),
      });

      await axios.post(`${apiUrl}api/users/UpdateVerifierActivity`, {
        Verifier_id: verifierId,
        LoginedTime: localStorage.getItem("loginedDateTime"),
        ActivityLevel: localStorage.getItem("activityPercentage"),
        BreaksConsumed: this.breakTime,
      });
    } catch (err) {
      // console.log("Logout API error:", err);
    }
    this.events.forEach((event) =>
      window.removeEventListener(event, this.eventHandler)
    );
    document.removeEventListener("visibilitychange", this.handleVisibility);

    localStorage.clear();
    store.dispatch(logoutUser());
  }

  activityMonitor() {
    setInterval(() => {
      this.updateActivityLevel();
    }, 60000); // Changed interval to 1 minute (60000 ms)
  }

  updateActivityLevel() {
    const now = Date.now();
    const loginTime = this.getStoredLoginTime() || now;
    
    // Calculate total time and active time
    const totalTimeInMinutes = (now - loginTime) / (60 * 1000);
    const activeTimeInMinutes = totalTimeInMinutes - (this.breakTime || 0);
    
    // Calculate base activity percentage
    let activityPercentage = Math.min(100, (activeTimeInMinutes / totalTimeInMinutes) * 100);
    
    // Factor in recent activity
    const timeSinceLastActivity = now - this.lastActivityTime;
    if (timeSinceLastActivity <= 30000) { // Active in last 30 seconds
      activityPercentage = Math.min(100, activityPercentage + 2);
    } else if (timeSinceLastActivity > this.breakThreshold) { // Inactive for more than break threshold
      activityPercentage = Math.max(0, activityPercentage - 5);
    }
    
    // Round to whole number
    activityPercentage = Math.round(activityPercentage);
    
    // Update stored values
    this.activityPercentage = activityPercentage;
    localStorage.setItem("activityPercentage", Math.round(activityPercentage).toString());

    // console.log('Activity Update:', {
    //   totalTime: Math.round(totalTimeInMinutes) + ' minutes',
    //   activeTime: Math.round(activeTimeInMinutes) + ' minutes',
    //   timeSinceLastActivity: Math.round(timeSinceLastActivity/1000) + ' seconds',
    //   activityPercentage: Math.round(activityPercentage) + '%'
    // });

    // Update server
    axios
      .post(`${apiUrl}api/users/UpdateVerifierActivity`, {
        Verifier_id: localStorage.getItem("verifierid"),
        LoginedTime: localStorage.getItem("loginedDateTime"),
        ActivityLevel: Math.round(activityPercentage),
        BreaksConsumed: Math.round(this.breakTime)
      })
      .catch((err) => {
        // console.error("Error updating activity level:", err);
      });
  }

  getActivityPercentage() {
    // Calculate percentage based on time since last activity (Web Work method)
    const now = Date.now();
    if (!this.lastActivityTime) {
      const stored = localStorage.getItem("activityPercentage");
      return stored !== null ? parseInt(stored, 10) : 0;
    }
    const lastActivity = this.lastActivityTime || this.startTime || now;
    const elapsed = now - lastActivity;
    // Web Work method: 100 - Math.floor((elapsed / idleThreshold) * 100)
    const percent = Math.max(
      0,
      100 - Math.floor((elapsed / this.idleThreshold) * 100)
    );
    return percent;
  }

  startBreak() {
    if (!this.isOnBreak) {
      const now = Date.now();
      this.isOnBreak = true;
      // Set break start time to exactly when the 1-minute threshold was crossed
      this.breakStartTime = now;
      
      // Store break state
      localStorage.setItem('breakStartTime', this.breakStartTime.toString());
      localStorage.setItem('isOnBreak', 'true');
      
      // console.log('===== BREAK STARTED =====');
      // console.log('Last activity:', new Date(this.lastActivityTime).toLocaleTimeString());
      // console.log('Break started at:', new Date(this.breakStartTime).toLocaleTimeString());
      // console.log('Current break time:', Math.round(this.breakTime * 100) / 100, 'minutes');
    }
  }

  endBreak() {
    if (this.isOnBreak && this.breakStartTime) {
      const breakEndTime = Date.now();
      const breakDuration = (breakEndTime - this.breakStartTime) / (60 * 1000); // Convert to minutes
      
      // Update break time (rounded to 2 decimal places)
      this.breakTime = Math.round(((parseFloat(this.breakTime) || 0) + breakDuration) * 100) / 100;
      
      // console.log('===== BREAK ENDED =====');
      // console.log('Break ended at:', new Date(breakEndTime).toLocaleTimeString());
      // console.log('Break duration:', Math.round(breakDuration * 100) / 100, 'minutes');
      // console.log('New total break time:', this.breakTime, 'minutes');
      
      // Store updated break time
      localStorage.setItem('breakTime', this.breakTime.toString());
      
      // Update break time on server
      const verifierId = localStorage.getItem("verifierid");
      if (verifierId) {
        axios.post(`${apiUrl}api/users/UpdateVerifierActivity`, {
          Verifier_id: verifierId,
          LoginedTime: localStorage.getItem("loginedDateTime"),
          ActivityLevel: Math.round(localStorage.getItem("activityPercentage")),
          BreaksConsumed: Math.round(this.breakTime)
        }).catch(err => {
          console.error("Error updating break time:", err);
        });
      }

      // Reset break state
      this.isOnBreak = false;
      this.breakStartTime = null;
      localStorage.removeItem('breakStartTime');
      localStorage.setItem('isOnBreak', 'false');
    }
  }

  // Helper method to get break time in minutes with 2 decimal places
  getFormattedBreakTime() {
    return this.breakTime.toFixed(2);
  }
}

export default IdleTimer;
